import { useState, useEffect, useRef } from 'react';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoUploader = ({ onUpload, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentImage || '');
  // Keep the URL input in sync with the current image when it is a remote URL.
  // We deliberately do NOT show internal /api/files/... or /api/products/.../image
  // proxy paths in the URL field to avoid confusing the user.
  const isExternalUrl = (val) =>
    typeof val === 'string' &&
    /^https?:\/\//i.test(val) &&
    !val.startsWith(`${API_URL}/api/`) &&
    !val.startsWith('/api/');
  const [urlInput, setUrlInput] = useState(isExternalUrl(currentImage) ? currentImage : '');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Re-sync when parent loads a product asynchronously
  useEffect(() => {
    setPreview(currentImage || '');
    setUrlInput(isExternalUrl(currentImage) ? currentImage : '');
  }, [currentImage]);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Format non supporté. Utilisez JPG, PNG ou WEBP.');
      return;
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 5MB)');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = `${API_URL}${response.data.url}`;
      setUrlInput(''); // clear URL field — image now comes from internal storage
      onUpload(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload');
      setPreview(currentImage || '');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const clearImage = () => {
    setPreview('');
    setUrlInput('');
    onUpload('');
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      {preview && (
        <div className="relative aspect-square max-w-[200px] bg-[color:var(--color-surface)] border border-white/10 overflow-hidden">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Replace with a clear visual error indicator instead of a
              // misleading generic photo. The URL is still saved in form state.
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.img-error-placeholder')) {
                const ph = document.createElement('div');
                ph.className = 'img-error-placeholder absolute inset-0 flex flex-col items-center justify-center text-xs text-amber-400 px-2 text-center gap-1';
                ph.innerHTML = '<span>Image inaccessible</span><span class="text-zinc-500">(URL enregistrée)</span>';
                parent.appendChild(ph);
              }
            }}
            onLoad={(e) => {
              e.target.style.display = '';
              const ph = e.target.parentElement?.querySelector('.img-error-placeholder');
              if (ph) ph.remove();
            }}
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 transition-colors"
            data-testid="clear-photo"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {/* Upload Buttons */}
      <div className="flex gap-2">
        {/* Camera - for mobile */}
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
          className="flex-1 flex items-center justify-center gap-2 border border-white/20 text-zinc-400 px-4 py-3 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] transition-colors disabled:opacity-50"
          data-testid="camera-button"
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Camera className="w-5 h-5" />
          )}
          <span className="hidden sm:inline">Caméra</span>
        </button>

        {/* Gallery */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-1 flex items-center justify-center gap-2 border border-white/20 text-zinc-400 px-4 py-3 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] transition-colors disabled:opacity-50"
          data-testid="upload-button"
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Upload className="w-5 h-5" />
          )}
          <span className="hidden sm:inline">Galerie</span>
        </button>
      </div>

      {/* Hidden Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileInput}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
        className="hidden"
      />

      {/* Error */}
      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      {/* URL Fallback */}
      <div>
        <p className="text-zinc-600 text-xs mb-2">Ou entrez une URL:</p>
        <input
          type="url"
          placeholder="https://..."
          value={urlInput}
          onChange={(e) => {
            const val = e.target.value;
            setUrlInput(val);
            setPreview(val);
            onUpload(val);
          }}
          className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]"
          data-testid="url-input"
        />
      </div>
    </div>
  );
};

export default PhotoUploader;
